import InfoIcon from '@mui/icons-material/Info';
import { Box, DialogActions, DialogContent, Stack, Typography } from '@mui/material';
import { useTranslate } from 'react-admin';

import { EMPLOYEES_BULK_CREATE_FILE_DATA_STARTING_LINE } from '@Common/constants/b2b/employees';
import { PostEmployeesBulkCreateValidateEndpoint } from '@Common/types/bo/b2b/employees/bulk_create';

import { Button, DialogTitleWithX } from '@sh4res/bo-common/components';
import { DetailsRowGrid } from '@sh4res/bo-common/components/B2bEmployeeBulkCreate/components/DetailsRowGrid';
import { useErrorHighlightStyles } from '@sh4res/bo-common/components/B2bEmployeeBulkCreate/components/highlightStyles';
import { useResourceBasePath } from '@sh4res/bo-common/hooks';

interface Props {
  data: PostEmployeesBulkCreateValidateEndpoint['Client']['ResponseBody'] & {
    type: 'validation_error';
  };
  cancel: () => void;
  close: () => void;
}

export function Errors(props: Props) {
  const translate = useTranslate();
  const styles = useErrorHighlightStyles();
  const resourceBasePath = useResourceBasePath();

  return (
    <>
      <DialogTitleWithX onClose={props.close}>
        {translate(
          'resources.bo_common.b2b.employees.actions.bulk_create.sections.validation_errors.title',
        )}
      </DialogTitleWithX>

      <DialogContent>
        <Stack direction="row" spacing={2} paddingTop={1}>
          <Stack direction="column" justifyContent="space-between">
            <Box>
              <Typography variant="subtitle2" color="#FF2D55">
                <span style={{ fontWeight: 700 }}>
                  {translate(
                    'resources.bo_common.b2b.employees.actions.bulk_create.sections.validation_errors.subtitle',
                  )}
                  :
                </span>{' '}
                {Object.keys(props.data.errors).length} value(s)
              </Typography>
              <Typography variant="body2">
                {translate(
                  'resources.bo_common.b2b.employees.actions.bulk_create.sections.validation_errors.description',
                )}
              </Typography>
            </Box>
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
              <InfoIcon htmlColor="#0009" />
              <Typography variant="subtitle2" color="#0009">
                {translate(
                  'resources.bo_common.b2b.employees.actions.bulk_create.sections.validation_errors.info',
                )}
              </Typography>
            </Stack>
          </Stack>
          <Box maxWidth={'75%'}>
            <DetailsRowGrid
              action="error"
              rawData={props.data.raw_data}
              shouldRenderRow={(idx) => idx in props.data.errors}
              rowNumberForIndex={(idx) => idx + EMPLOYEES_BULK_CREATE_FILE_DATA_STARTING_LINE}
              info={(idx) => props.data.errors[idx]}
              highlightRowClassName={styles.highlightedRow}
              highlightCellClassName={styles.highlightedCell}
              sx={{ maxHeight: '75vh', overflowY: 'scroll' }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={props.cancel}
          label="resources.bo_common.b2b.employees.actions.bulk_create.sections.validation_errors.cancel"
          variant="text"
          permissions={{
            resource: `${resourceBasePath}employees/bulk_create`,
            action: 'create',
          }}
          data-testid="BoExternalB2bEmployeeBulkCreateErrors_CancelButton"
        />
      </DialogActions>
    </>
  );
}
